import { useMemo, useState } from "react";
import { renderizarValor } from "../../functions/dataFunctions";
import { estadoDeUnidad, ETIQUETAS_ESTADO } from "../../services/cols/estado";

const MAX_OPCIONES_SELECT = 10;

// La columna que dice si la unidad está activa. Es la única que la tabla
// entiende semánticamente: de ahí salen el color de la franja de la fila y
// el orden del resumen de arriba.
export const ES_COLUMNA_ESTADO = (key) =>
  key === "estado" || key === "estadoReal";

// Orden en que se muestran los estados en el resumen. Primero lo que anda,
// último lo que ya no existe; en el medio lo que hay que mirar.
// `chocado` va con lo que hay que mirar e `irreparable` con lo que ya no
// existe, y esa separación es la que importa: de un chocado se vuelve y de un
// irreparable no. Juntos, «chocado» se leería como el final de esa unidad.
const ORDEN_ESTADO = ["activo", "sin-actividad", "chocado", "sin-dato",
                      "baja", "irreparable", "vendido"];

/* De qué color va cada estado, tanto en la franja de la fila como en el
 * contador de arriba. Sólo se pinta lo que pide atención: una flota sana es
 * una lista sin colores, y eso también es información. */
const TONOS = {
  activo: "",
  "sin-actividad": "alerta",
  chocado: "alerta",
  "sin-dato": "falta",
  baja: "critico",
  irreparable: "critico",
  vendido: "critico",
};

export const tonoDeFila = (row, columnaEstado) => {
  if (!columnaEstado) return "";
  const bruto = row?.[columnaEstado];
  const texto = String(bruto ?? "").trim().toLowerCase();
  const porTexto = Object.entries(ETIQUETAS_ESTADO).find(
    ([, etiqueta]) => etiqueta.toLowerCase() === texto,
  );
  return TONOS[porTexto ? porTexto[0] : estadoDeUnidad(bruto)] ?? "";
};

/**
 * Filtros para listados/tablas.
 *
 * - Búsqueda de texto libre sobre toda la fila.
 * - Columnas marcadas `filtroEspecial: true` en los headers se vuelven
 *   automáticamente:
 *     - un <select> de valores exactos, si la columna tiene pocos
 *       valores únicos en los datos actuales (ej: Estado, Marca, Tipo)
 *     - un chip que "escopea" la búsqueda de texto a esa columna,
 *       si tiene muchos valores únicos (ej: DNI, Dominio, Legajo —
 *       identificadores, no categorías)
 * - Un resumen arriba, que además de contar, filtra. Ver más abajo.
 */
export const useFiltrosTabla = ({
  rows = [],
  headers = null,
  textoInicial = "",
}) => {
  /* La búsqueda puede arrancar puesta, y es lo que hace que el buscador del
   * hub pueda mandar a `/padron/personas?buscar=…` y caer con la persona ya
   * filtrada. Se lee UNA vez, al montar: atado a la URL en cada render, tocar
   * el filtro tendría que reescribir la dirección y el botón de atrás pasaría
   * a deshacer tecleos en vez de salir de la pantalla. Misma decisión que
   * `?vista=` en el RTO. */
  const [textoFiltro, setTextoFiltro] = useState(textoInicial);
  /* Un filtro puede arrancar puesto (`filtroPorDefecto` en el header). Lo usa
   * el padrón de personas para abrir en "Activo": de 1.000 filas se trabaja
   * con 191, y una lista donde el 80% son bajas no la mira nadie. */
  const [filtroSelect, setFiltroSelect] = useState(() =>
    Object.fromEntries(
      (headers ?? [])
        .filter((h) => h.filtroEspecial && h.filtroPorDefecto)
        .map((h) => [h.key, h.filtroPorDefecto]),
    ),
  );
  const [filtroScope, setFiltroScope] = useState({});

  const columnasFiltroEspecial = useMemo(
    () => headers?.filter((h) => h.filtroEspecial) ?? [],
    [headers],
  );

  const facetas = useMemo(() => {
    return columnasFiltroEspecial.map((col) => {
      const valoresUnicos = [
        ...new Set(
          rows
            .map((r) => renderizarValor(r[col.key], col.key))
            .filter((v) => v !== undefined && v !== null && v !== "" && v !== "-"),
        ),
      ].sort((a, b) => String(a).localeCompare(String(b), "es"));

      const modo =
        valoresUnicos.length > 0 && valoresUnicos.length <= MAX_OPCIONES_SELECT
          ? "select"
          : "scope";

      return { ...col, modo, opciones: valoresUnicos };
    });
  }, [columnasFiltroEspecial, rows]);

  const setSelect = (key, valor) =>
    setFiltroSelect((prev) => ({ ...prev, [key]: valor }));

  const toggleScope = (key) =>
    setFiltroScope((prev) => ({ ...prev, [key]: !prev[key] }));

  /* La columna sobre la que se arma el resumen de arriba. Se prefiere el
   * estado, que es la que todos miran primero; si el listado no tiene estado
   * —eventos, taller— se usa la primera columna categórica que haya (el
   * sector, el tipo de evento). Si no hay ninguna, no hay resumen: es mejor
   * no mostrar nada que inventar un contador. */
  const columnaResumen = useMemo(() => {
    const selects = facetas.filter((c) => c.modo === "select");
    return selects.find((c) => ES_COLUMNA_ESTADO(c.key)) ?? selects[0] ?? null;
  }, [facetas]);

  const columnaEstado = useMemo(
    () => facetas.find((c) => ES_COLUMNA_ESTADO(c.key))?.key ?? null,
    [facetas],
  );

  // Aplica los filtros de <select> salvo el de la columna que se indique.
  const aplicarSelects = useMemo(
    () => (lista, exceptoKey) =>
      facetas.reduce((acc, col) => {
        if (col.modo !== "select") return acc;
        if (col.key === exceptoKey) return acc;
        const elegido = filtroSelect[col.key];
        if (!elegido) return acc;
        return acc.filter(
          (row) => renderizarValor(row[col.key], col.key) === elegido,
        );
      }, lista),
    [facetas, filtroSelect],
  );

  const aplicarTexto = useMemo(
    () => (lista) => {
      if (!textoFiltro) return lista;
      const filtro = textoFiltro.toLowerCase();
      const scopeActivo = facetas.filter(
        (c) => c.modo === "scope" && filtroScope[c.key],
      );

      return lista.filter((row) => {
        if (scopeActivo.length > 0) {
          return scopeActivo.some((col) =>
            renderizarValor(row[col.key], col.key)
              ?.toString()
              .toLowerCase()
              .includes(filtro),
          );
        }

        return Object.entries(row).some(([key, value]) =>
          renderizarValor(value, key)?.toString().toLowerCase().includes(filtro),
        );
      });
    },
    [facetas, filtroScope, textoFiltro],
  );

  /* LA BÚSQUEDA LE GANA AL FILTRO.
   *
   * Buscar por nombre es preguntar por alguien concreto. Contestar "no hay
   * resultados" porque el filtro de Estado lo tapaba es el peor no que puede
   * dar un buscador: quien busca "Sabatto" ya sabe que existe, y lo que
   * concluye es que el sistema no lo tiene.
   *
   * No se apaga el filtro —sigue puesto y a la vista para cuando se borre la
   * búsqueda—: se muestran igual los que se escapan, y se DICE cuántos son.
   * Ampliar la lista en silencio haría dudar de si el filtro está andando. */
  const { dataFiltrada, escapanDelFiltro } = useMemo(() => {
    const conFiltros = aplicarTexto(aplicarSelects(rows, null));
    if (!textoFiltro) return { dataFiltrada: conFiltros, escapanDelFiltro: 0 };
    const soloTexto = aplicarTexto(rows);
    const extra = soloTexto.length - conFiltros.length;
    return extra > 0
      ? { dataFiltrada: soloTexto, escapanDelFiltro: extra }
      : { dataFiltrada: conFiltros, escapanDelFiltro: 0 };
  }, [rows, aplicarSelects, aplicarTexto, textoFiltro]);

  /* EL RESUMEN CUENTA SOBRE TODO MENOS SOBRE SÍ MISMO.
   *
   * Los contadores se calculan con los demás filtros puestos pero SIN el de
   * su propia columna. Si se contara sobre `dataFiltrada`, al tocar "De baja"
   * los otros contadores pasarían a cero y el resumen dejaría de servir justo
   * cuando se lo está usando: uno toca un contador para ir y volver, y para
   * volver necesita seguir viendo cuántos hay del otro lado. */
  const resumen = useMemo(() => {
    if (!columnaResumen) return null;
    const base = aplicarTexto(aplicarSelects(rows, columnaResumen.key));
    const cuenta = new Map();
    base.forEach((row) => {
      const v = renderizarValor(row[columnaResumen.key], columnaResumen.key);
      if (v === undefined || v === null || v === "" || v === "-") return;
      cuenta.set(v, (cuenta.get(v) ?? 0) + 1);
    });

    const esEstado = ES_COLUMNA_ESTADO(columnaResumen.key);
    const items = [...cuenta.entries()]
      .map(([valor, cantidad]) => {
        const clave = esEstado
          ? (Object.entries(ETIQUETAS_ESTADO).find(
              ([, etiqueta]) =>
                etiqueta.toLowerCase() === String(valor).toLowerCase(),
            )?.[0] ?? estadoDeUnidad(valor))
          : null;
        return { valor, cantidad, tono: clave ? (TONOS[clave] ?? "") : "" , clave };
      })
      .sort((a, b) =>
        esEstado
          ? ORDEN_ESTADO.indexOf(a.clave) - ORDEN_ESTADO.indexOf(b.clave)
          : b.cantidad - a.cantidad,
      );

    return {
      columna: columnaResumen.key,
      label: columnaResumen.label,
      total: base.length,
      items,
    };
  }, [rows, columnaResumen, aplicarSelects, aplicarTexto]);

  const hayFiltrosActivos =
    !!textoFiltro ||
    Object.values(filtroSelect).some(Boolean) ||
    Object.values(filtroScope).some(Boolean);

  const limpiarFiltros = () => {
    setTextoFiltro("");
    setFiltroSelect({});
    setFiltroScope({});
  };

  return {
    textoFiltro,
    setTextoFiltro,
    facetas,
    filtroSelect,
    setSelect,
    filtroScope,
    toggleScope,
    dataFiltrada,
    escapanDelFiltro,
    hayFiltrosActivos,
    limpiarFiltros,
    resumen,
    columnaEstado,
  };
};
